// src/utils/tokens/rollingMetrics.ts

import type { SeriesData } from './dataProcessing';

/**
 * Compute rolling volatility (annualized percentage)
 */
export function computeRollingVolatility(
  returns: number[],
  window: number,
  minPoints: number = 20
): number[] {
  const result: number[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const rets = returns.slice(Math.max(0, i - window + 1), i + 1).filter((r) => Number.isFinite(r));
    
    if (rets.length >= minPoints) {
      const mean = rets.reduce((a, b) => a + b, 0) / rets.length;
      const variance = rets.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / (rets.length - 1);
      result.push(Math.sqrt(variance) * Math.sqrt(365) * 100); // Annualized %
    } else {
      result.push(NaN);
    }
  }
  
  return result;
}

/**
 * Compute rolling Sharpe ratio
 */
export function computeRollingSharpe(
  returns: number[],
  window: number,
  minPoints: number = 20
): number[] {
  const result: number[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const rets = returns.slice(Math.max(0, i - window + 1), i + 1).filter((r) => Number.isFinite(r));
    
    if (rets.length >= minPoints) {
      const mean = rets.reduce((a, b) => a + b, 0) / rets.length;
      const variance = rets.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / (rets.length - 1);
      const vol = Math.sqrt(variance);
      result.push(vol > 0 ? (mean / vol) * Math.sqrt(365) : NaN);
    } else {
      result.push(NaN);
    }
  }
  
  return result;
}

/**
 * Compute rolling Sortino ratio
 */
export function computeRollingSortino(
  returns: number[],
  window: number,
  minPoints: number = 20
): number[] {
  const result: number[] = [];
  
  for (let i = 0; i < returns.length; i++) {
    const rets = returns.slice(Math.max(0, i - window + 1), i + 1).filter((r) => Number.isFinite(r));
    
    if (rets.length >= minPoints) {
      const mean = rets.reduce((a, b) => a + b, 0) / rets.length;
      const downside = rets.filter((r) => r < 0);
      
      if (downside.length > 0) {
        // Use sample variance (N-1) to match backend SQL
        const downsideVar = downside.reduce((sum, r) => sum + r * r, 0) / (rets.length - 1);
        const downsideVol = Math.sqrt(downsideVar);
        result.push(downsideVol > 0 ? (mean / downsideVol) * Math.sqrt(365) : NaN);
      } else {
        result.push(NaN);
      }
    } else {
      result.push(NaN);
    }
  }
  
  return result;
}

/**
 * Compute rolling drawdown (percentage from peak)
 */
export function computeRollingDrawdown(values: number[]): number[] {
  const drawdown: number[] = [];
  let peak = -Infinity;
  
  for (let i = 0; i < values.length; i++) {
    const val = values[i];
    if (val !== undefined && Number.isFinite(val)) {
      if (val > peak) peak = val;
      if (peak > 0) {
        drawdown.push(((val - peak) / peak) * 100); // Percentage
      } else {
        drawdown.push(NaN);
      }
    } else {
      drawdown.push(NaN);
    }
  }
  
  return drawdown;
}

/**
 * Compute all rolling metrics for a set of tickers
 * @param alignedSeriesWithLookback - Full series with lookback data for computation
 * @param alignedSeriesDisplay - Display series (without lookback) for dates
 * @param dailyReturnsMap - Returns computed from full series with lookback
 * @param tickers - List of tickers to compute for
 */
export function computeAllRollingMetrics(
  alignedSeriesWithLookback: Record<string, SeriesData>,
  alignedSeriesDisplay: Record<string, SeriesData>,
  dailyReturnsMap: Record<string, number[]>,
  tickers: string[]
): Record<string, Record<string, SeriesData>> {
  const metrics: Record<string, Record<string, SeriesData>> = {
    'computed:vol_30d': {},
    'computed:vol_90d': {},
    'computed:sharpe_30d': {},
    'computed:sharpe_90d': {},
    'computed:sortino_30d': {},
    'computed:sortino_90d': {},
    'computed:drawdown': {},
  };

  for (const ticker of tickers) {
    const seriesWithLookback = alignedSeriesWithLookback[ticker];
    const seriesDisplay = alignedSeriesDisplay[ticker];
    const returns = dailyReturnsMap[ticker];
    
    if (!seriesWithLookback || !seriesDisplay || !returns || returns.length < 30) continue;

    const datesDisplay = seriesDisplay.dates;
    const valuesWithLookback = seriesWithLookback.values;

    // Compute rolling metrics on full data with lookback
    const vol30Full = computeRollingVolatility(returns, 30, 20);
    const vol90Full = computeRollingVolatility(returns, 90, 60);
    const sharpe30Full = computeRollingSharpe(returns, 30, 20);
    const sharpe90Full = computeRollingSharpe(returns, 90, 60);
    const sortino30Full = computeRollingSortino(returns, 30, 20);
    const sortino90Full = computeRollingSortino(returns, 90, 60);
    const drawdownFull = computeRollingDrawdown(valuesWithLookback);

    // Slice to display range (remove lookback period)
    const vol30 = vol30Full.slice(-datesDisplay.length);
    const vol90 = vol90Full.slice(-datesDisplay.length);
    const sharpe30 = sharpe30Full.slice(-datesDisplay.length);
    const sharpe90 = sharpe90Full.slice(-datesDisplay.length);
    const sortino30 = sortino30Full.slice(-datesDisplay.length);
    const sortino90 = sortino90Full.slice(-datesDisplay.length);
    const drawdown = drawdownFull.slice(-datesDisplay.length);

    // Store results with display dates
    if (metrics['computed:vol_30d']) metrics['computed:vol_30d'][ticker] = { dates: datesDisplay, values: vol30 };
    if (metrics['computed:vol_90d']) metrics['computed:vol_90d'][ticker] = { dates: datesDisplay, values: vol90 };
    if (metrics['computed:sharpe_30d']) metrics['computed:sharpe_30d'][ticker] = { dates: datesDisplay, values: sharpe30 };
    if (metrics['computed:sharpe_90d']) metrics['computed:sharpe_90d'][ticker] = { dates: datesDisplay, values: sharpe90 };
    if (metrics['computed:sortino_30d']) metrics['computed:sortino_30d'][ticker] = { dates: datesDisplay, values: sortino30 };
    if (metrics['computed:sortino_90d']) metrics['computed:sortino_90d'][ticker] = { dates: datesDisplay, values: sortino90 };
    if (metrics['computed:drawdown']) metrics['computed:drawdown'][ticker] = { dates: datesDisplay, values: drawdown };
  }

  return metrics;
}

